import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { errorCallback } from 'src/app/app-const';
import { ApiEnvelope } from '../academy/models/permissions.model';
import {
  SupportAgent,
  SupportCatalogCategory,
  SupportCatalogCategoryPayload,
  SupportCatalogLookup,
  SupportCatalogRequestType,
  SupportCatalogRequestTypePayload,
  SupportEventFilter,
  SupportMessage,
  SupportMessagePayload,
  SupportPage,
  SupportPagination,
  SupportRoute,
  SupportSettings,
  SupportSlaPolicy,
  SupportTeam,
  SupportTeamMember,
  SupportTeamPayload,
  SupportTicketDetail,
  SupportTicketEvent,
  SupportTicketPatch,
  SupportTicketQuery,
  SupportTicketRow,
  SupportViewCounts,
} from '../models/support.model';

export const SUPPORT_TICKETS_PER_PAGE = 25;

/** Client for the support desk agent API (`/api/v1/super-admin/support`). */
@Injectable({ providedIn: 'root' })
export class SupportService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  private get baseUrl(): string {
    return `${this.rootUrl}/api/v1/super-admin/support`;
  }

  // ----------------------------------------------------
  // Inbox and tickets
  // ----------------------------------------------------

  getViewCounts(): Observable<SupportViewCounts> {
    return this.data(this.http.get<ApiEnvelope<SupportViewCounts>>(`${this.baseUrl}/views/counts`));
  }

  listTickets(query: SupportTicketQuery = {}): Observable<SupportPage<SupportTicketRow>> {
    const params = this.requestBuilder({ per_page: SUPPORT_TICKETS_PER_PAGE, ...query });
    return this.page(this.http.get<ApiEnvelope<SupportPage<SupportTicketRow>>>(`${this.baseUrl}/tickets`, { params }));
  }

  getTicket(ref: string): Observable<SupportTicketDetail> {
    return this.data(this.http.get<ApiEnvelope<SupportTicketDetail>>(`${this.baseUrl}/tickets/${this.encode(ref)}`));
  }

  listEvents(
    ref: string,
    filter: SupportEventFilter | null = null,
    page = 1,
    perPage: number | null = null,
  ): Observable<SupportPage<SupportTicketEvent>> {
    const params = this.requestBuilder({ filter, page, per_page: perPage });
    return this.page(
      this.http.get<ApiEnvelope<SupportPage<SupportTicketEvent>>>(`${this.baseUrl}/tickets/${this.encode(ref)}/events`, { params }),
    );
  }

  /** Public reply or internal note, sent as multipart so attachments travel with it. */
  postMessage(ref: string, payload: SupportMessagePayload): Observable<SupportMessage> {
    const body = new FormData();
    body.append('kind', payload.kind);
    body.append('body', payload.body);
    if (payload.kind === 'internal') {
      (payload.mention_user_ids || []).forEach((id) => body.append('mention_user_ids[]', String(id)));
    }
    (payload.attachments || []).forEach((file) => body.append('attachments[]', file, file.name));
    return this.data(this.http.post<ApiEnvelope<SupportMessage>>(`${this.baseUrl}/tickets/${this.encode(ref)}/messages`, body));
  }

  updateTicket(ref: string, patch: SupportTicketPatch): Observable<SupportTicketDetail> {
    return this.data(this.http.patch<ApiEnvelope<SupportTicketDetail>>(`${this.baseUrl}/tickets/${this.encode(ref)}`, patch));
  }

  listAgents(): Observable<SupportAgent[]> {
    return this.data(this.http.get<ApiEnvelope<SupportAgent[]>>(`${this.baseUrl}/agents`)).pipe(map((rows) => rows || []));
  }

  downloadAttachment(id: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.baseUrl}/attachments/${id}`, { observe: 'response', responseType: 'blob' })
      .pipe(catchError((err) => this.fail(err)));
  }

  // ----------------------------------------------------
  // Teams, routing, SLA and settings
  // ----------------------------------------------------

  listTeams(): Observable<SupportTeam[]> {
    return this.data(this.http.get<ApiEnvelope<SupportTeam[]>>(`${this.baseUrl}/teams`)).pipe(map((rows) => rows || []));
  }

  createTeam(payload: SupportTeamPayload): Observable<SupportTeam> {
    return this.data(this.http.post<ApiEnvelope<SupportTeam>>(`${this.baseUrl}/teams`, payload));
  }

  updateTeam(id: number, payload: SupportTeamPayload): Observable<SupportTeam> {
    return this.data(this.http.put<ApiEnvelope<SupportTeam>>(`${this.baseUrl}/teams/${id}`, payload));
  }

  /** Not routed through errorCallback: the caller shows the 422 "still routed" message itself. */
  deleteTeam(id: number): Observable<void> {
    return this.http.delete(`${this.baseUrl}/teams/${id}`).pipe(map(() => void 0));
  }

  updateTeamMembers(id: number, members: Pick<SupportTeamMember, 'user_id' | 'is_available'>[]): Observable<SupportTeam> {
    return this.data(this.http.put<ApiEnvelope<SupportTeam>>(`${this.baseUrl}/teams/${id}/members`, { members }));
  }

  getRouting(): Observable<SupportRoute[]> {
    return this.data(this.http.get<ApiEnvelope<SupportRoute[]>>(`${this.baseUrl}/routing`)).pipe(map((rows) => rows || []));
  }

  saveRouting(routes: SupportRoute[]): Observable<SupportRoute[]> {
    return this.data(this.http.put<ApiEnvelope<SupportRoute[]>>(`${this.baseUrl}/routing`, { routes }));
  }

  getSlaPolicies(): Observable<SupportSlaPolicy[]> {
    return this.data(this.http.get<ApiEnvelope<SupportSlaPolicy[]>>(`${this.baseUrl}/sla-policies`)).pipe(map((rows) => rows || []));
  }

  saveSlaPolicies(policies: SupportSlaPolicy[]): Observable<SupportSlaPolicy[]> {
    return this.data(this.http.put<ApiEnvelope<SupportSlaPolicy[]>>(`${this.baseUrl}/sla-policies`, { policies }));
  }

  // Managed categories and request types (PF-0164)

  /** Names and links for labels and filters; readable with the support view permission. */
  getCatalogLookup(): Observable<SupportCatalogLookup> {
    return this.data(this.http.get<ApiEnvelope<SupportCatalogLookup>>(`${this.baseUrl}/catalog`)).pipe(
      map((body) => ({ categories: body?.categories || [], types: body?.types || [] })),
    );
  }

  listCatalogCategories(): Observable<SupportCatalogCategory[]> {
    return this.data(this.http.get<ApiEnvelope<{ items: SupportCatalogCategory[] }>>(`${this.baseUrl}/settings/categories`)).pipe(
      map((body) => body?.items || []),
    );
  }

  createCatalogCategory(payload: SupportCatalogCategoryPayload): Observable<SupportCatalogCategory> {
    return this.data(this.http.post<ApiEnvelope<{ item: SupportCatalogCategory }>>(`${this.baseUrl}/settings/categories`, payload)).pipe(
      map((body) => body.item),
    );
  }

  updateCatalogCategory(id: number, payload: SupportCatalogCategoryPayload): Observable<SupportCatalogCategory> {
    return this.data(
      this.http.put<ApiEnvelope<{ item: SupportCatalogCategory }>>(`${this.baseUrl}/settings/categories/${id}`, payload),
    ).pipe(map((body) => body.item));
  }

  /** Not routed through errorCallback: the caller explains a 409 "still in use" itself. */
  deleteCatalogCategory(id: number): Observable<void> {
    return this.http.delete(`${this.baseUrl}/settings/categories/${id}`).pipe(map(() => void 0));
  }

  listCatalogRequestTypes(): Observable<SupportCatalogRequestType[]> {
    return this.data(this.http.get<ApiEnvelope<{ items: SupportCatalogRequestType[] }>>(`${this.baseUrl}/settings/request-types`)).pipe(
      map((body) => body?.items || []),
    );
  }

  createCatalogRequestType(payload: SupportCatalogRequestTypePayload): Observable<SupportCatalogRequestType> {
    return this.data(
      this.http.post<ApiEnvelope<{ item: SupportCatalogRequestType }>>(`${this.baseUrl}/settings/request-types`, payload),
    ).pipe(map((body) => body.item));
  }

  updateCatalogRequestType(id: number, payload: SupportCatalogRequestTypePayload): Observable<SupportCatalogRequestType> {
    return this.data(
      this.http.put<ApiEnvelope<{ item: SupportCatalogRequestType }>>(`${this.baseUrl}/settings/request-types/${id}`, payload),
    ).pipe(map((body) => body.item));
  }

  deleteCatalogRequestType(id: number): Observable<void> {
    return this.http.delete(`${this.baseUrl}/settings/request-types/${id}`).pipe(map(() => void 0));
  }

  getSettings(): Observable<SupportSettings> {
    return this.data(this.http.get<ApiEnvelope<SupportSettings>>(`${this.baseUrl}/settings`));
  }

  saveSettings(settings: SupportSettings): Observable<SupportSettings> {
    return this.data(this.http.put<ApiEnvelope<SupportSettings>>(`${this.baseUrl}/settings`, settings));
  }

  // ----------------------------------------------------
  // Helpers
  // ----------------------------------------------------

  private data<T>(request: Observable<ApiEnvelope<T>>): Observable<T> {
    return request.pipe(
      map((response) => this.unwrap(response)),
      catchError((err) => this.fail(err)),
    );
  }

  private page<T>(request: Observable<ApiEnvelope<SupportPage<T>>>): Observable<SupportPage<T>> {
    return request.pipe(
      map((response) => this.normalizePaginated(this.unwrap(response))),
      catchError((err) => this.fail(err)),
    );
  }

  private fail(err: unknown): Observable<never> {
    errorCallback(err);
    return throwError(() => err);
  }

  private unwrap<T>(response: ApiEnvelope<T>): T {
    return response?.data as T;
  }

  /** Normalizes the `{items, pagination}` list shape used by the support API. */
  private normalizePaginated<T>(payload: SupportPage<T> | undefined): SupportPage<T> {
    const items = payload?.items ?? [];
    const fallback: SupportPagination = { current_page: 1, last_page: 1, per_page: SUPPORT_TICKETS_PER_PAGE, total: items.length };
    return {
      items,
      pagination: {
        current_page: payload?.pagination?.current_page ?? fallback.current_page,
        last_page: payload?.pagination?.last_page ?? fallback.last_page,
        per_page: payload?.pagination?.per_page ?? fallback.per_page,
        total: payload?.pagination?.total ?? fallback.total,
      },
    };
  }

  private encode(ref: string): string {
    return encodeURIComponent(ref);
  }

  protected requestBuilder(params: object): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value)) {
        value
          .filter((entry) => entry !== undefined && entry !== null && entry !== '')
          .forEach((entry) => (httpParams = httpParams.append(`${key}[]`, String(entry))));
        return;
      }
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }
}
